import React from 'react';
import { Page, Text, View, Image, Link } from '@react-pdf/renderer';
import {
  PageHeader,
  PageFooter,
  styles,
  PageHeaderLogo,
  ActionItems,
} from '../constants';
import { Notes } from '../constants';
import { keys } from '../../translator/translation_keys';
import { VehicleCard } from '../vehicle';
import { Category } from './assessment_category';
import { Signatures } from './assessment_signatures';
import { useTranslation } from 'react-i18next';
import { imageExtensions } from '../../files';
import { textStyles } from '../styles';
import { customColors } from '../../misc';
import {
  formatBundleItems,
  getCategoryConditionalTriggers,
  isCategoryVisible,
} from '../../inspection';

export default function AssessmentContent({
  company,
  workspace,
  inspection = {},
  notes,
  actionItems,
  supplementary = false,
  props = [],
}) {
  const { t } = useTranslation();
  const referenceFiles = [
    ...(inspection.template?.images || []),
    ...(inspection.attachments?.filter(
      (a) => a.fileType !== 'AUDIO' && a.extension !== 'SIGNATURE',
    ) || []),
  ];
  const reviewers = inspection?.reviewers ?? [];

  const itemsWithActionItems = inspection.items?.map((item) => ({
    ...item,
    actionItems: (actionItems || []).filter(
      (ai) => ai.inspectionItemId === item.id,
    ),
  }));

  const { itemMap } = inspection.items?.length
    ? formatBundleItems(inspection.items)
    : { itemMap: {} };
  const categoryConditionalTriggers = getCategoryConditionalTriggers(
    inspection.template?.categories,
  );

  const Label = ({ text, backgroundColor }) => {
    return (
      <View
        style={[
          textStyles.label,
          {
            backgroundColor,
          },
        ]}
      >
        <Text style={{ color: 'white' }}>{text}</Text>
      </View>
    );
  };

  const Reviewer = ({ reviewer }) => {
    const { user, status, notes } = reviewer;
    const name = user?.firstName + ' ' + user?.lastName;
    const semiBold = {
      fontSize: 8,
      color: '#1d1d1f',
      fontFamily: 'eina03semibold',
    };

    return (
      <View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
          }}
        >
          <Text style={semiBold}>{name}: </Text>
          <Text style={semiBold}>{status}</Text>
        </View>
        <Text style={styles.text}>"{notes}"</Text>
      </View>
    );
  };

  return (
    <Page size="A4" style={styles.page}>
      {!supplementary && (
        <PageHeaderLogo company={company} workspace={workspace} />
      )}
      <PageHeader
        company={company}
        data={inspection}
        title={inspection.template?.title}
        workspace={workspace}
        type="Assessment"
        revision={inspection?.template?.revisionNumber}
      />
      <View>
        <Text style={styles.titleHeader}>{inspection.template?.title}</Text>
        <Text style={styles.headerText}>
          {inspection.template?.description}
        </Text>
      </View>
      {!!props?.includes('labels') && !!inspection.labels?.length && (
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
            marginTop: '3',
          }}
        >
          {inspection.labels.map((l) => {
            return (
              <Label
                key={l.id}
                text={l.name || 'Label'}
                backgroundColor={
                  customColors[l.color] || customColors.DARK_GRAY
                }
              />
            );
          })}
        </View>
      )}
      {!!inspection.vehicle ? (
        <VehicleCard vehicle={inspection?.vehicle} />
      ) : null}
      <View style={styles.categoryContainer}>
        {inspection.template?.categories?.map((category, index) => {
          if (
            !isCategoryVisible(category, categoryConditionalTriggers, itemMap)
          ) {
            return null;
          }
          const selectedCat = props?.includes(category.id);
          return selectedCat || !!supplementary ? (
            <Category
              key={`${category.id}-${index}`}
              category={category.title}
              categoryId={category.id}
              isBundle={category.isBundle}
              questions={category.questions}
              answers={itemsWithActionItems}
              props={props}
            />
          ) : null;
        })}
      </View>
      {props?.includes('notes') && (
        <View style={styles.categoryContainer}>
          <Notes notes={notes} style={styles.categoryContainer} />
          <Text style={styles.text}>{inspection.notes}</Text>
        </View>
      )}
      {actionItems?.length &&
        supplementary === false &&
        props?.includes('actions') && (
          <View style={styles.categoryContainer}>
            <ActionItems actionItems={actionItems || []} />
          </View>
        )}
      {referenceFiles?.length && props?.includes('files') && (
        <View style={styles.categoryContainer}>
          <Text style={styles.categoryHeader}>
            {t(keys.common.REFERENCE_FILES)}
          </Text>
          <View style={styles.templateImageContainer}>
            {referenceFiles?.map((file, index) => {
              const { imageUrl, url, extension, name, id } = file || {};
              const src = imageUrl || url;
              const ext = extension || src?.split('.')?.pop();
              return imageExtensions.includes(ext) ? (
                <Image
                  style={styles.templateImage}
                  src={src}
                  key={`${id}-${index}`}
                />
              ) : (
                <View key={id}>
                  <Text style={styles.text}>{`File Type: ${extension}`}</Text>
                  <Link style={styles.linkLeft} src={src}>
                    {name}
                  </Link>
                </View>
              );
            })}
          </View>
        </View>
      )}
      {props?.includes('reviews') && (
        <View style={styles.categoryContainer}>
          <Text style={{ ...styles.categoryHeader, marginBottom: 0 }}>
            {t(keys.common.REVIEWERS)}
          </Text>
          {reviewers?.length ? (
            reviewers?.map((reviewer) => <Reviewer reviewer={reviewer} />)
          ) : (
            <Text style={styles.text}>{t(keys.common.NONE)}</Text>
          )}
        </View>
      )}
      {props?.includes('signatures') && (
        <View style={styles.categoryContainer}>
          <Signatures
            signatures={
              inspection.attachments?.filter(
                (a) => a.extension === 'SIGNATURE',
              ) || []
            }
          />
        </View>
      )}
      <PageFooter />
    </Page>
  );
}
